/**
 * Temas de Vuetify - UNMSM
 *
 * Definiciones de temas light y dark basados en la identidad visual institucional
 */

import type { ThemeDefinition } from 'vuetify'

/**
 * Tema claro UNMSM
 * Incluye paleta completa con tonalidades de colores institucionales y semánticos
 */
export const unmsm: ThemeDefinition = {
  dark: false,
  colors: {
    // Colores base de Vuetify (requeridos)
    background: '#f8f8f8',
    surface: '#FFFFFF',
    'surface-bright': '#FFFFFF',
    'surface-light': '#f1f1f1',
    'surface-variant': '#e9e9e9',
    'on-surface-variant': '#646464',

    // Colores de marca - Rojo institucional
    primary: '#d94c4c',
    'primary-lighten-5': '#fdeded',
    'primary-lighten-4': '#f9d2d2',
    'primary-lighten-3': '#f3baba',
    'primary-lighten-2': '#eda1a1',
    'primary-lighten-1': '#e47c7c',
    'primary-darken-1': '#c53838',
    'primary-darken-2': '#a32b2b',
    'primary-darken-3': '#892727',
    'primary-darken-4': '#732424',
    'primary-darken-5': '#3e0f0f',

    // Colores de marca - Dorado académico
    accent: '#eab12b',
    'accent-lighten-5': '#fefcf3',
    'accent-lighten-4': '#fcf6d9',
    'accent-lighten-3': '#f9ebae',
    'accent-lighten-2': '#f5d97a',
    'accent-lighten-1': '#efc14b',
    'accent-darken-1': '#d5981d',
    'accent-darken-2': '#b1781b',
    'accent-darken-3': '#91601d',
    'accent-darken-4': '#784f1b',
    'accent-darken-5': '#452a0b',

    // Alias para secondary (compatibilidad Vuetify)
    secondary: '#eab12b',
    'secondary-lighten-5': '#fefcf3',
    'secondary-lighten-4': '#fcf6d9',
    'secondary-lighten-3': '#f9ebae',
    'secondary-lighten-2': '#f5d97a',
    'secondary-lighten-1': '#efc14b',
    'secondary-darken-1': '#d5981d',
    'secondary-darken-2': '#b1781b',
    'secondary-darken-3': '#91601d',
    'secondary-darken-4': '#784f1b',
    'secondary-darken-5': '#452a0b',

    // Paleta de neutros
    'gray-50': '#f8f8f8',
    'gray-100': '#f1f1f1',
    'gray-200': '#e9e9e9',
    'gray-300': '#d6d6d6',
    'gray-400': '#b5b5b5',
    'gray-500': '#9e9e9e',
    'gray-600': '#7e7e7e',
    'gray-700': '#646464',
    'gray-800': '#4b4b4b',
    'gray-900': '#3C3C3C',
    'gray-950': '#222222',

    // Colores semánticos - Success
    success: '#009723',
    'success-lighten-5': '#e6f6eb',
    'success-lighten-4': '#b3e4c2',
    'success-lighten-3': '#80d299',
    'success-lighten-2': '#4dc070',
    'success-lighten-1': '#26b04a',
    'success-darken-1': '#007f1d',
    'success-darken-2': '#006718',
    'success-darken-3': '#004f12',
    'success-darken-4': '#00370d',
    'success-darken-5': '#001f07',

    // Colores semánticos - Error/Danger
    error: '#ef4444',
    'error-lighten-5': '#fef2f2',
    'error-lighten-4': '#fee2e2',
    'error-lighten-3': '#fecaca',
    'error-lighten-2': '#fca5a5',
    'error-lighten-1': '#f87171',
    'error-darken-1': '#dc2626',
    'error-darken-2': '#b91c1c',
    'error-darken-3': '#991b1b',
    'error-darken-4': '#7f1d1d',
    'error-darken-5': '#450a0a',

    danger: '#ef4444',
    'danger-lighten-5': '#fef2f2',
    'danger-lighten-4': '#fee2e2',
    'danger-lighten-3': '#fecaca',
    'danger-lighten-2': '#fca5a5',
    'danger-lighten-1': '#f87171',
    'danger-darken-1': '#dc2626',
    'danger-darken-2': '#b91c1c',
    'danger-darken-3': '#991b1b',
    'danger-darken-4': '#7f1d1d',
    'danger-darken-5': '#450a0a',

    // Colores semánticos - Warning
    warning: '#f59e0b',
    'warning-lighten-5': '#fffbeb',
    'warning-lighten-4': '#fef3c7',
    'warning-lighten-3': '#fde68a',
    'warning-lighten-2': '#fcd34d',
    'warning-lighten-1': '#fbbf24',
    'warning-darken-1': '#d97706',
    'warning-darken-2': '#b45309',
    'warning-darken-3': '#92400e',
    'warning-darken-4': '#78350f',
    'warning-darken-5': '#451a03',

    // Colores semánticos - Info
    info: '#3b82f6',
    'info-lighten-5': '#eff6ff',
    'info-lighten-4': '#dbeafe',
    'info-lighten-3': '#bfdbfe',
    'info-lighten-2': '#93c5fd',
    'info-lighten-1': '#60a5fa',
    'info-darken-1': '#2563eb',
    'info-darken-2': '#1d4ed8',
    'info-darken-3': '#1e40af',
    'info-darken-4': '#1e3a8a',
    'info-darken-5': '#172554',

    // Color de acción - Gris pizarra para elementos interactivos
    action: '#374151',
    'action-lighten-5': '#f3f4f6',
    'action-lighten-4': '#e5e7eb',
    'action-lighten-3': '#d1d5db',
    'action-lighten-2': '#9ca3af',
    'action-lighten-1': '#6b7280',
    'action-darken-1': '#1f2937',
    'action-darken-2': '#111827',
    'action-darken-3': '#0d131e',
    'action-darken-4': '#080c12',
    'action-darken-5': '#030507',
  },

  variables: {
    // Opacidades de interacción
    'border-opacity': 0.12,
    'high-emphasis-opacity': 0.87,
    'medium-emphasis-opacity': 0.60,
    'disabled-opacity': 0.38,
    'idle-opacity': 0.04,
    'hover-opacity': 0.04,
    'focus-opacity': 0.12,
    'selected-opacity': 0.08,
    'activated-opacity': 0.12,
    'pressed-opacity': 0.12,
    'dragged-opacity': 0.08,

    // Bordes
    'border-color': '#e9e9e9',

    // Código y elementos especiales
    'theme-kbd': '#222222',
    'theme-on-kbd': '#FFFFFF',
    'theme-code': '#f8f8f8',
    'theme-on-code': '#222222',
  }
}

/**
 * Tema oscuro UNMSM
 * Adaptación del tema principal para modo oscuro
 */
export const unmsmDark: ThemeDefinition = {
  dark: true,
  colors: {
    // Colores base modo oscuro
    background: '#121212',
    surface: '#1e1e1e',
    'surface-bright': '#2c2c2c',
    'surface-light': '#262626',
    'surface-variant': '#3c3c3c',
    'on-surface-variant': '#b5b5b5',

    // Colores de marca (ajustados para mejor contraste)
    primary: '#e47c7c',
    'primary-lighten-5': '#fdeded',
    'primary-lighten-4': '#f9d2d2',
    'primary-lighten-3': '#f3baba',
    'primary-lighten-2': '#eda1a1',
    'primary-lighten-1': '#e47c7c',
    'primary-darken-1': '#d94c4c',
    'primary-darken-2': '#c53838',
    'primary-darken-3': '#a32b2b',
    'primary-darken-4': '#892727',
    'primary-darken-5': '#732424',

    accent: '#efc14b',
    'accent-lighten-5': '#fefcf3',
    'accent-lighten-4': '#fcf6d9',
    'accent-lighten-3': '#f9ebae',
    'accent-lighten-2': '#f5d97a',
    'accent-lighten-1': '#efc14b',
    'accent-darken-1': '#eab12b',
    'accent-darken-2': '#d5981d',
    'accent-darken-3': '#b1781b',
    'accent-darken-4': '#91601d',
    'accent-darken-5': '#784f1b',

    secondary: '#efc14b',
    'secondary-lighten-5': '#fefcf3',
    'secondary-lighten-4': '#fcf6d9',
    'secondary-lighten-3': '#f9ebae',
    'secondary-lighten-2': '#f5d97a',
    'secondary-lighten-1': '#efc14b',
    'secondary-darken-1': '#eab12b',
    'secondary-darken-2': '#d5981d',
    'secondary-darken-3': '#b1781b',
    'secondary-darken-4': '#91601d',
    'secondary-darken-5': '#784f1b',

    // Grises invertidos para dark mode
    'gray-50': '#222222',
    'gray-100': '#3C3C3C',
    'gray-200': '#4b4b4b',
    'gray-300': '#646464',
    'gray-400': '#7e7e7e',
    'gray-500': '#9e9e9e',
    'gray-600': '#b5b5b5',
    'gray-700': '#d6d6d6',
    'gray-800': '#e9e9e9',
    'gray-900': '#f1f1f1',
    'gray-950': '#f8f8f8',

    // Semánticos (colores más brillantes para dark)
    success: '#4dc070',
    'success-lighten-5': '#e6f6eb',
    'success-lighten-4': '#b3e4c2',
    'success-lighten-3': '#80d299',
    'success-lighten-2': '#4dc070',
    'success-lighten-1': '#26b04a',
    'success-darken-1': '#009723',
    'success-darken-2': '#007f1d',
    'success-darken-3': '#006718',
    'success-darken-4': '#004f12',
    'success-darken-5': '#00370d',

    error: '#f87171',
    'error-lighten-5': '#fef2f2',
    'error-lighten-4': '#fee2e2',
    'error-lighten-3': '#fecaca',
    'error-lighten-2': '#fca5a5',
    'error-lighten-1': '#f87171',
    'error-darken-1': '#ef4444',
    'error-darken-2': '#dc2626',
    'error-darken-3': '#b91c1c',
    'error-darken-4': '#991b1b',
    'error-darken-5': '#7f1d1d',

    danger: '#f87171',
    'danger-lighten-5': '#fef2f2',
    'danger-lighten-4': '#fee2e2',
    'danger-lighten-3': '#fecaca',
    'danger-lighten-2': '#fca5a5',
    'danger-lighten-1': '#f87171',
    'danger-darken-1': '#ef4444',
    'danger-darken-2': '#dc2626',
    'danger-darken-3': '#b91c1c',
    'danger-darken-4': '#991b1b',
    'danger-darken-5': '#7f1d1d',

    warning: '#fbbf24',
    'warning-lighten-5': '#fffbeb',
    'warning-lighten-4': '#fef3c7',
    'warning-lighten-3': '#fde68a',
    'warning-lighten-2': '#fcd34d',
    'warning-lighten-1': '#fbbf24',
    'warning-darken-1': '#f59e0b',
    'warning-darken-2': '#d97706',
    'warning-darken-3': '#b45309',
    'warning-darken-4': '#92400e',
    'warning-darken-5': '#78350f',

    info: '#60a5fa',
    'info-lighten-5': '#eff6ff',
    'info-lighten-4': '#dbeafe',
    'info-lighten-3': '#bfdbfe',
    'info-lighten-2': '#93c5fd',
    'info-lighten-1': '#60a5fa',
    'info-darken-1': '#3b82f6',
    'info-darken-2': '#2563eb',
    'info-darken-3': '#1d4ed8',
    'info-darken-4': '#1e40af',
    'info-darken-5': '#1e3a8a',

    // Color de acción - Gris pizarra para elementos interactivos (más brillante para dark)
    action: '#6b7280',
    'action-lighten-5': '#f3f4f6',
    'action-lighten-4': '#e5e7eb',
    'action-lighten-3': '#d1d5db',
    'action-lighten-2': '#9ca3af',
    'action-lighten-1': '#6b7280',
    'action-darken-1': '#374151',
    'action-darken-2': '#1f2937',
    'action-darken-3': '#111827',
    'action-darken-4': '#0d131e',
    'action-darken-5': '#080c12',
  },

  variables: {
    'border-opacity': 0.12,
    'high-emphasis-opacity': 0.87,
    'medium-emphasis-opacity': 0.60,
    'disabled-opacity': 0.38,
    'idle-opacity': 0.10,
    'hover-opacity': 0.08,
    'focus-opacity': 0.12,
    'selected-opacity': 0.12,
    'activated-opacity': 0.16,
    'pressed-opacity': 0.16,
    'dragged-opacity': 0.12,
    'border-color': '#3c3c3c',
    'theme-kbd': '#f8f8f8',
    'theme-on-kbd': '#222222',
    'theme-code': '#1e1e1e',
    'theme-on-code': '#f8f8f8',
  }
}
